import type { ISODateRange } from 'shared/src/date-string-utils/types';
import {
  Channel,
  channelById,
  Rate,
  rateForTacticUnitPriceTypeId,
  tacticById
} from 'shared/src/domain/line-item/line-item-channels';
import type { ProposalInfo } from '@/features/proposals/proposal-info-context';
import { PacingType } from 'shared/src/domain/line-item/line-item-types';
import {
  generateMonthlyBlocks,
  splitCurrentPacingBlock
} from 'shared/src/domain/line-item/pacing-helpers';
import {
  addPacingBlock,
  clearPacingBlocks,
  removePacingBlock,
  syncLifetimeBlock,
  updatePacingBlock
} from '@/features/line-items/line-item-form/common/pacing-helpers';
import { PacingBlockV2 } from 'shared/src/api/line-item-types';
import { PacingBlockClient } from '@/features/line-items/line-item-form/common/form-state';
import {
  ExecutionStatus,
  PacingValidatorSpend
} from 'shared/src/domain/line-item/pacing-validator';
import {
  selectIsLocked,
  selectZeroDollarBudget
} from '@/features/line-items/line-item-form/common/selectors';

export type CommonAction =
  | { type: 'SET_NAME'; value: string }
  | { type: 'CHANGE_CHANNEL'; channel_id: number }
  | { type: 'CHANGE_TACTIC'; tactic_id: number }
  | { type: 'CHANGE_UNIT_PRICE_TYPE'; unit_price_type_id: number }
  | { type: 'SET_MEDIA_PLATFORMS'; media_platform_ids: number[] }
  | { type: 'SET_PLANNED_UNIT_COST'; value: number | undefined }
  | { type: 'SET_DATES'; value: ISODateRange }
  | { type: 'SET_GEO'; value: string | undefined }
  | { type: 'SET_TARGETING'; value: string | undefined }
  | { type: 'SET_AUDIENCE'; value: string | undefined }
  | { type: 'SET_AD_FORMATS'; value: string | undefined }
  | { type: 'SET_NOTES'; value: string | undefined }
  | { type: 'SET_PO_NUMBER'; value: string | undefined }
  | { type: 'TOUCH_FIELD'; field: string }
  | { type: 'BLUR_FIELD'; field: string }
  | { type: 'PACING_SET_TYPE'; value: 'lifetime' | 'custom' }
  | { type: 'PACING_TOGGLE_MONTHLY_ROLLOVER' }
  | { type: 'PACING_ADD_BLOCK' }
  | { type: 'PACING_UPDATE_BLOCK'; index: number; patch: Partial<PacingBlockV2> }
  | { type: 'PACING_REMOVE_BLOCK'; index: number }
  | { type: 'SEED_MONTHLY_BLOCKS' }
  | { type: 'CLEAR_PACING_BLOCKS' };

type StandardAction =
  | CommonAction
  | { type: 'SET_PRICE'; value: number | undefined }
  | { type: 'SET_UNIT_PRICE'; value: number | undefined }
  | { type: 'TOGGLE_MARGIN_LOCK' }
  | { type: 'RESET'; initialState: StandardFormState };

type MgmtFeeAction =
  | CommonAction
  | { type: 'SET_PRICE'; value: number | undefined }
  | { type: 'SET_MEDIA_BUDGET'; value: number | undefined }
  | { type: 'RESET'; initialState: MgmtFeeFormState };

type ZeroDollarAction =
  | CommonAction
  | { type: 'SET_ESTIMATED_UNITS'; value: number | undefined }
  | { type: 'RESET'; initialState: ZeroDollarFormState };

export type CommonLineItemState = {
  name: string;
  channel_id: number | null;
  tactic_id: number | null;
  unit_price_type_id: number | null;
  planned_unit_cost: number | undefined;
  dates: ISODateRange;
  pacing_type: 'lifetime' | 'custom';
  monthly_rollover: boolean;
  geo: string | undefined;
  targeting: string | undefined;
  audience: string | undefined;
  ad_formats: string | undefined;
  notes: string | undefined;
  media_platform_ids: number[] | undefined;
  purchase_order_number: string | undefined;
  pacing_blocks: PacingBlockClient[];
};

export type StandardLineItemState = CommonLineItemState & {
  price: number | undefined;
  unit_price: number | undefined;
  marginLocked: boolean;
};

export type MgmtFeeLineItemState = CommonLineItemState & {
  price: number | undefined;
  media_budget: number | undefined;
};

export type ZeroDollarLineItemState = CommonLineItemState & {
  estimated_units: number | undefined;
};

export type CommonFormState = {
  mode: 'create' | 'edit';
  execution_status: ExecutionStatus;
  touched: ReadonlySet<string>;
  dailyBudgetSpent: PacingValidatorSpend;
  channels: Channel[];
  proposalInfo: ProposalInfo;
};

export type CommonReducerState = CommonFormState & {
  formData: CommonLineItemState;
};

export type StandardFormState = CommonFormState & {
  existing:
    | { type: 'existing'; id: string; data: StandardLineItemState }
    | { type: 'new'; data: StandardLineItemState };
  formData: StandardLineItemState;
};

export type MgmtFeeFormState = CommonFormState & {
  existing:
    | { type: 'existing'; id: string; data: MgmtFeeLineItemState }
    | { type: 'new'; data: MgmtFeeLineItemState };
  formData: MgmtFeeLineItemState;
};

export type ZeroDollarFormState = CommonFormState & {
  existing:
    | { type: 'existing'; id: string; data: ZeroDollarLineItemState }
    | { type: 'new'; data: ZeroDollarLineItemState };
  formData: ZeroDollarLineItemState;
};

export function standardLineItemReducer(
  state: StandardFormState,
  action: StandardAction
): StandardFormState {
  switch (action.type) {
    case 'SET_UNIT_PRICE':
      return setUnitPrice(state, action.value);
    case 'SET_PRICE':
      return setStandardPrice(state, action.value);
    case 'SET_PLANNED_UNIT_COST':
      return setStandardPlannedUnitCost(state, action.value);
    case 'SEED_MONTHLY_BLOCKS':
      return seedStandardMonthlyBlocks(state);
    case 'TOGGLE_MARGIN_LOCK':
      return toggleMarginLock(state);
    case 'RESET':
      return action.initialState;
    default:
      return cascadeStandardValues(commonReducer(state, action), action);
  }
}

export function mgmtFeeLineItemReducer(
  state: MgmtFeeFormState,
  action: MgmtFeeAction
): MgmtFeeFormState {
  switch (action.type) {
    case 'SET_PRICE':
      return updateFormData(state, 'price', action.value);
    case 'SET_MEDIA_BUDGET':
      return setMediaBudget(state, action.value);
    case 'SET_PLANNED_UNIT_COST':
      return updateFormData(state, 'planned_unit_cost', action.value);
    case 'SEED_MONTHLY_BLOCKS':
      return seedMgmtFeeMonthlyBlocks(state);
    case 'RESET':
      return action.initialState;
    default:
      return cascadeMgmtFeeValues(commonReducer(state, action), action);
  }
}

export function zeroDollarLineItemReducer(
  state: ZeroDollarFormState,
  action: ZeroDollarAction
): ZeroDollarFormState {
  switch (action.type) {
    case 'SET_ESTIMATED_UNITS':
      return setEstimatedUnits(state, action.value);
    case 'SET_PLANNED_UNIT_COST':
      return updateFormData(state, 'planned_unit_cost', action.value);
    case 'SEED_MONTHLY_BLOCKS':
      return seedZeroDollarMonthlyBlocks(state);
    case 'RESET':
      return action.initialState;
    default:
      return cascadeZeroDollarValues(commonReducer(state, action), action);
  }
}

export function commonReducer<T extends CommonReducerState>(state: T, action: CommonAction): T {
  switch (action.type) {
    case 'TOUCH_FIELD':
      return { ...state, touched: new Set([...state.touched, action.field]) };
    case 'BLUR_FIELD':
      return { ...state, touched: new Set([...state.touched, action.field]) };
    case 'SET_NAME':
      return updateFormData(state, 'name', action.value);
    case 'SET_PO_NUMBER':
      return updateFormData(state, 'purchase_order_number', action.value);
    case 'CHANGE_CHANNEL':
      return changeChannel(state, action.channel_id);
    case 'CHANGE_TACTIC':
      return changeTactic(state, action.tactic_id);
    case 'CHANGE_UNIT_PRICE_TYPE':
      return changeUnitPriceType(state, action.unit_price_type_id);
    case 'SET_DATES':
      return setDates(state, action.value);
    case 'SET_GEO':
      return updateFormData(state, 'geo', action.value);
    case 'SET_TARGETING':
      return updateFormData(state, 'targeting', action.value);
    case 'SET_AUDIENCE':
      return updateFormData(state, 'audience', action.value);
    case 'SET_AD_FORMATS':
      return updateFormData(state, 'ad_formats', action.value);
    case 'SET_MEDIA_PLATFORMS':
      return updateFormData(state, 'media_platform_ids', action.media_platform_ids);
    case 'SET_PLANNED_UNIT_COST':
      return updateFormData(state, 'planned_unit_cost', action.value);
    case 'SET_NOTES':
      return updateFormData(state, 'notes', action.value);
    case 'PACING_SET_TYPE':
      return setPacingType(state, action.value);
    case 'PACING_TOGGLE_MONTHLY_ROLLOVER':
      return updateFormData(state, 'monthly_rollover', !state.formData.monthly_rollover);
    case 'PACING_ADD_BLOCK':
      return addPacingBlock(state);
    case 'PACING_REMOVE_BLOCK':
      return removePacingBlock(state, action.index);
    case 'PACING_UPDATE_BLOCK':
      return updatePacingBlock(state, action.index, action.patch);
    case 'SEED_MONTHLY_BLOCKS':
      throw new Error('SEED_MONTHLY_BLOCKS should be handled in parent');
    case 'CLEAR_PACING_BLOCKS':
      return clearPacingBlocks(state);
  }
}

function cascadeStandardValues(
  state: StandardFormState,
  action: StandardAction
): StandardFormState {
  if (isRateCascadeAction(action)) {
    // Auto Populate the unit price rate (or set to undefined)
    const rate = findRate(state);
    return updateStandardFormData(state, {
      unit_price: rate ? rate.unitPrice : undefined,
      planned_unit_cost: rate ? rate.unitPrice * (1 - rate.targetMargin) : undefined
    });
  } else if (action.type === 'SET_DATES' || action.type === 'PACING_SET_TYPE') {
    return syncLifetimeBlock(state, state.formData.price);
  } else {
    return state;
  }
}

function cascadeMgmtFeeValues(state: MgmtFeeFormState, action: MgmtFeeAction): MgmtFeeFormState {
  if (isRateCascadeAction(action)) {
    // Auto Populate the planned_unit_cost based on rate card (or set to undefined)
    return updateFormData(state, 'planned_unit_cost', calcRateCardUnitCost(state));
  } else if (action.type === 'SET_DATES' || action.type === 'PACING_SET_TYPE') {
    return syncLifetimeBlock(state, state.formData.media_budget);
  } else {
    return state;
  }
}

function cascadeZeroDollarValues(
  state: ZeroDollarFormState,
  action: ZeroDollarAction
): ZeroDollarFormState {
  if (isRateCascadeAction(action)) {
    // Auto Populate the planned_unit_cost based on rate card (or set to undefined), then
    // resync the lifetime block so it tracks the derived budget instead of stranding the old
    // blocks against a budget that has collapsed to undefined.
    const next = updateFormData(state, 'planned_unit_cost', calcRateCardUnitCost(state));
    return syncLifetimeBlock(next, selectZeroDollarBudget(next));
  } else if (action.type === 'SET_DATES' || action.type === 'PACING_SET_TYPE') {
    return syncLifetimeBlock(state, selectZeroDollarBudget(state));
  } else {
    return state;
  }
}

function isRateCascadeAction(action: { type: string }) {
  return (
    action.type === 'CHANGE_CHANNEL' ||
    action.type === 'CHANGE_TACTIC' ||
    action.type === 'CHANGE_UNIT_PRICE_TYPE'
  );
}

function calcRateCardUnitCost(state: CommonReducerState): number | undefined {
  const rate = findRate(state);
  return rate ? rate.unitPrice * (1 - rate.targetMargin) : undefined;
}

function seedStandardMonthlyBlocks(state: StandardFormState) {
  const { dates, price } = state.formData;
  if (!dates || !price) return state;
  else return updateFormData(state, 'pacing_blocks', monthlyBlocks(dates, price));
}

function seedMgmtFeeMonthlyBlocks(state: MgmtFeeFormState) {
  const { dates, media_budget } = state.formData;
  if (!dates || !media_budget) return state;
  else return updateFormData(state, 'pacing_blocks', monthlyBlocks(dates, media_budget));
}

function seedZeroDollarMonthlyBlocks(state: ZeroDollarFormState) {
  const { dates, estimated_units, planned_unit_cost } = state.formData;
  if (!dates || !estimated_units || !planned_unit_cost) {
    return state;
  } else {
    const media_budget = estimated_units * planned_unit_cost;
    return updateFormData(state, 'pacing_blocks', monthlyBlocks(dates, media_budget));
  }
}

function monthlyBlocks(dates: ISODateRange, budget: number) {
  return generateMonthlyBlocks(dates, budget).map(b => ({ ...b, origin: 'pending' as const }));
}

// Lifetime pacing keeps a single block that tracks the price as the user types. Custom pacing
// is left untouched on a price change — the buyer rebalances the blocks manually, and the
// validator surfaces a totals_mismatch until they sum to the new price.
function setStandardPrice(state: StandardFormState, price: number | undefined): StandardFormState {
  return syncLifetimeBlock(updateFormData(state, 'price', price), price);
}

function setMediaBudget(
  state: MgmtFeeFormState,
  media_budget: number | undefined
): MgmtFeeFormState {
  return syncLifetimeBlock(updateFormData(state, 'media_budget', media_budget), media_budget);
}

function setEstimatedUnits(
  state: ZeroDollarFormState,
  estimated_units: number | undefined
): ZeroDollarFormState {
  const { planned_unit_cost } = state.formData;
  const media_budget =
    planned_unit_cost && estimated_units ? planned_unit_cost * estimated_units : undefined;
  return syncLifetimeBlock(updateFormData(state, 'estimated_units', estimated_units), media_budget);
}

function changeChannel<T extends CommonReducerState>(state: T, channel_id: number): T {
  const channel = channelById(state.channels, channel_id);
  return {
    ...state,
    formData: {
      ...state.formData,
      channel_id: channel.id,
      tactic_id: null,
      unit_price_type_id: null,
      media_platform_ids: []
    }
  };
}

function changeTactic<T extends CommonReducerState>(state: T, tactic_id: number) {
  const { channel_id } = state.formData;
  if (!channel_id) throw new Error('cannot set tactic without channel_id');
  const channel = channelById(state.channels, channel_id);
  const tactic = channel.tactics.find(tactic => tactic.id === tactic_id);
  if (!tactic) throw new Error(`tactic_id ${tactic_id} not valid for channel_id ${channel_id}`);

  return {
    ...state,
    formData: {
      ...state.formData,
      tactic_id: tactic_id,
      // Also autoselect the unit_price_type_id if there is only one option
      ...(tactic.rates.length === 1
        ? { unit_price_type_id: tactic.rates[0].unitPriceType.id }
        : {}),
      media_platform_ids: tactic.platforms.length === 1 ? [tactic.platforms[0].id] : []
    }
  };
}

function changeUnitPriceType<T extends CommonReducerState>(state: T, unit_price_type_id: number) {
  const { tactic_id } = state.formData;
  const tactic = tacticById(state.channels, tactic_id!);
  if (!tactic.rates.find(rate => rate.unitPriceType.id === unit_price_type_id))
    throw new Error(`unit_price_type_id ${unit_price_type_id} not valid for tactic ${tactic_id}`);
  return updateFormData(state, 'unit_price_type_id', unit_price_type_id);
}

function setUnitPrice(state: StandardFormState, value: number | undefined): StandardFormState {
  const { marginLocked } = state.formData;
  if (value == null) {
    return updateStandardFormData(state, {
      unit_price: value,
      // If we clear the unit_price when the margin is locked, remove planned_unit_cost and unlock
      ...(marginLocked ? { marginLocked: false, planned_unit_cost: undefined } : {})
    });
  } else {
    return updateStandardFormData(state, {
      unit_price: value,
      ...(marginLocked ? { planned_unit_cost: calcPlannedUnitCost(state, value) } : {})
    });
  }
}

function toggleMarginLock(state: StandardFormState): StandardFormState {
  const { marginLocked, planned_unit_cost, unit_price } = state.formData;
  return !unit_price || !planned_unit_cost
    ? updateFormData(state, 'marginLocked', false)
    : updateFormData(state, 'marginLocked', !marginLocked);
}

function setStandardPlannedUnitCost(
  state: StandardFormState,
  value: number | undefined
): StandardFormState {
  const { marginLocked } = state.formData;
  if (value == null) {
    return updateStandardFormData(state, {
      planned_unit_cost: value,
      ...(marginLocked ? { marginLocked: false } : {})
    });
  } else {
    return updateStandardFormData(state, {
      planned_unit_cost: value,
      ...(marginLocked ? { unit_price: calcUnitPrice(state, value) } : {})
    });
  }
}

function setPacingType<T extends CommonReducerState>(state: T, value: PacingType): T {
  if (isPacingTypeLocked(state) || state.formData.pacing_type === value) {
    return state;
  } else if (value === 'custom') {
    return {
      ...state,
      formData: {
        ...state.formData,
        pacing_type: 'custom',
        pacing_blocks: selectIsLocked(state) ? splitLifetimeForToggle(state) : []
      }
    };
  } else {
    return updateFormData(state, 'pacing_type', 'lifetime');
  }
}

function isPacingTypeLocked<T extends CommonReducerState>(state: T): boolean {
  return selectIsLocked(state) && hasSpend(state) && state.formData.pacing_type === 'custom';
}

function splitLifetimeForToggle<T extends CommonReducerState>(state: T): PacingBlockClient[] {
  const { dates, pacing_blocks } = state.formData;
  if (pacing_blocks.length !== 1) return pacing_blocks;
  const total = pacing_blocks.reduce((sum, b) => sum + b.budget, 0);
  return splitCurrentPacingBlock<PacingBlockClient>({
    pacing_type: 'lifetime',
    pacing_blocks,
    dates,
    old_price: total,
    price: total,
    dailyBudgetSpent: state.dailyBudgetSpent
  });
}

function setDates<T extends CommonReducerState>(state: T, value: ISODateRange): T {
  // if dates are locked return state
  // if pacing type is 'lifetime' and isLocked, return state
  // if isLocked, don't allow changing the start date and don't allow setting end date before today
  return updateFormData(state, 'dates', value);
}

function calcPlannedUnitCost(state: StandardFormState, newUnitPrice: number): number | undefined {
  const { planned_unit_cost, unit_price } = state.formData;
  if (!unit_price || !planned_unit_cost) return planned_unit_cost;
  // Calculate the existing margin
  const margin = 1 - planned_unit_cost / unit_price;
  // Keep the new unit cost consistent with the planned margin
  return newUnitPrice * (1 - margin);
}

function calcUnitPrice(state: StandardFormState, newPlannedUnitCost: number): number | undefined {
  const { planned_unit_cost, unit_price } = state.formData;
  if (!unit_price || !planned_unit_cost) return planned_unit_cost;
  // Calculate the existing margin
  const margin = 1 - planned_unit_cost / unit_price;
  // Keep the new unit price consistent with the planned margin
  return newPlannedUnitCost / (1 - margin);
}

function findRate(state: CommonReducerState): Rate | undefined {
  const { tactic_id, unit_price_type_id } = state.formData;
  if (!tactic_id || !unit_price_type_id) return undefined;
  return rateForTacticUnitPriceTypeId(state.channels, tactic_id, unit_price_type_id);
}

function updateFormData<TState extends CommonReducerState, TKey extends keyof TState['formData']>(
  state: TState,
  key: TKey,
  value: TState['formData'][TKey]
): TState {
  return {
    ...state,
    formData: {
      ...state.formData,
      [key]: value
    }
  };
}

function updateStandardFormData(
  state: StandardFormState,
  update: Partial<StandardLineItemState>
): StandardFormState {
  return { ...state, formData: { ...state.formData, ...update } };
}

function hasSpend<T extends CommonReducerState>(state: T) {
  return state.dailyBudgetSpent.some(d => (d.amount ?? 0) > 0);
}
